import React, { useMemo } from 'react';
import { X, TrendingUp, Sparkles, Tag, BarChart3, PieChart, Lightbulb } from 'lucide-react';

const StatsDashboard = ({ ideas, onClose }) => {
  const stats = useMemo(() => {
    const totalIdeas = ideas.length;
    const activeIdeas = ideas.filter(i => !i.archived).length;
    const archivedIdeas = ideas.filter(i => i.archived).length;

    const categoryDistribution = {};
    ideas.forEach(idea => {
      categoryDistribution[idea.category] = (categoryDistribution[idea.category] || 0) + 1;
    });

    const tagFrequency = {};
    ideas.forEach(idea => {
      idea.tags.forEach(tag => {
        tagFrequency[tag] = (tagFrequency[tag] || 0) + 1;
      });
    });

    const now = new Date();
    const monthlyData = [];
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'short' });
      const monthIdeas = ideas.filter(idea => {
        const ideaDate = new Date(idea.created_at);
        return ideaDate.getMonth() === monthDate.getMonth() && 
               ideaDate.getFullYear() === monthDate.getFullYear();
      }).length;
      monthlyData.push({ month: monthName, count: monthIdeas });
    }

    const dayCount = {};
    ideas.forEach(idea => {
      const date = new Date(idea.created_at).toLocaleDateString();
      dayCount[date] = (dayCount[date] || 0) + 1;
    });
    const mostProductiveDay = Object.entries(dayCount).sort((a, b) => b[1] - a[1])[0];

    return {
      totalIdeas, activeIdeas, archivedIdeas,
      categoryDistribution, tagFrequency, monthlyData,
      mostProductiveDay: mostProductiveDay ? { date: mostProductiveDay[0], count: mostProductiveDay[1] } : null
    };
  }, [ideas]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Analytics Dashboard
          </h2>
          <p className="text-sm sm:text-base text-[var(--text-secondary)]">
            Insights into your idea journey
          </p>
        </div>
        <button
          data-testid="close-stats-btn"
          onClick={onClose}
          className="bg-[var(--surface)] text-[var(--text-primary)] font-bold px-4 sm:px-6 py-3 border-2 border-[var(--border-default)] action-btn-hover transition-all duration-150"
        >
          <X size={20} />
        </button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-[#007AFF]"></div>
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp size={20} className="text-[#007AFF]" />
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Total Ideas</span>
          </div>
          <div className="text-4xl font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {stats.totalIdeas}
          </div>
          <div className="text-xs text-[var(--text-secondary)] mt-2">
            {stats.activeIdeas} active, {stats.archivedIdeas} archived
          </div>
        </div>

        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF3B30]"></div>
          <div className="flex items-center gap-3 mb-2">
            <Sparkles size={20} className="text-[#FF3B30]" />
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Top Category</span>
          </div>
          <div className="text-2xl font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {Object.keys(stats.categoryDistribution).length > 0 
              ? Object.entries(stats.categoryDistribution).sort((a, b) => b[1] - a[1])[0][0]
              : 'None'}
          </div>
          <div className="text-xs text-[var(--text-secondary)] mt-2">
            {Object.keys(stats.categoryDistribution).length > 0
              ? `${Object.entries(stats.categoryDistribution).sort((a, b) => b[1] - a[1])[0][1]} ideas`
              : 'No ideas yet'}
          </div>
        </div>

        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-[#FFD60A]"></div>
          <div className="flex items-center gap-3 mb-2">
            <Tag size={20} className="text-[#FFD60A]" />
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Total Tags</span>
          </div>
          <div className="text-4xl font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {Object.keys(stats.tagFrequency).length}
          </div>
          <div className="text-xs text-[var(--text-secondary)] mt-2">
            Across all ideas
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 size={20} />
            <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Creation Trend
            </h3>
          </div>
          <div className="space-y-3">
            {stats.monthlyData.map((data, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-12 text-xs font-mono uppercase text-[var(--text-secondary)]">
                  {data.month}
                </div>
                <div className="flex-1 flex items-center gap-2">
                  <div 
                    className="h-8 bg-gradient-to-r from-[#007AFF] to-[#0055CC] border border-[var(--border-default)] transition-all duration-300"
                    style={{ width: `${stats.monthlyData.length > 0 ? (data.count / Math.max(...stats.monthlyData.map(d => d.count)) * 100) : 0}%`, minWidth: data.count > 0 ? '30px' : '0px' }}
                  ></div>
                  <span className="text-sm font-bold min-w-[20px]">{data.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Distribution */}
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
          <div className="flex items-center gap-2 mb-6">
            <PieChart size={20} />
            <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Category Distribution
            </h3>
          </div>
          <div className="space-y-3">
            {Object.entries(stats.categoryDistribution)
              .sort((a, b) => b[1] - a[1])
              .map(([category, count], idx) => {
                const colors = ['#FF3B30', '#007AFF', '#FFD60A', '#34C759', '#AF52DE', '#FF9500'];
                const percentage = ((count / stats.totalIdeas) * 100).toFixed(0);
                return (
                  <div key={category} className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 border border-[var(--border-default)]"
                      style={{ backgroundColor: colors[idx % colors.length] }}
                    ></div>
                    <div className="flex-1 flex items-center justify-between">
                      <span className="text-sm font-medium">{category}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-[var(--surface-alt)] border border-[var(--border-subtle)]">
                          <div 
                            className="h-full transition-all duration-300"
                            style={{ 
                              width: `${percentage}%`,
                              backgroundColor: colors[idx % colors.length]
                            }}
                          ></div>
                        </div>
                        <span className="text-xs font-mono w-12 text-right">{count} ({percentage}%)</span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Tag Cloud */}
      {Object.keys(stats.tagFrequency).length > 0 && (
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
          <div className="flex items-center gap-2 mb-6">
            <Tag size={20} />
            <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Tag Usage
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(stats.tagFrequency)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 20)
              .map(([tag, count]) => {
                const maxCount = Math.max(...Object.values(stats.tagFrequency));
                const size = Math.max(12, Math.min(32, (count / maxCount) * 32));
                return (
                  <div
                    key={tag}
                    className="bg-[var(--surface-alt)] border-2 border-[var(--border-default)] px-3 py-2 tag-hover transition-colors duration-150 cursor-pointer"
                    style={{ fontSize: `${size}px` }}
                  >
                    <span className="font-mono uppercase tracking-wider">{tag}</span>
                    <span className="ml-2 text-xs opacity-70">&times;{count}</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Insights */}
      {stats.mostProductiveDay && (
        <div className="bg-gradient-to-br from-[#007AFF]/10 to-[#FFD60A]/10 border-2 border-[var(--border-default)] p-6">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb size={20} className="text-[#007AFF]" />
            <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Insight
            </h3>
          </div>
          <p className="text-base text-[var(--text-primary)]">
            Your most productive day was <strong>{new Date(stats.mostProductiveDay.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong> with <strong>{stats.mostProductiveDay.count} {stats.mostProductiveDay.count === 1 ? 'idea' : 'ideas'}</strong> created!
          </p>
        </div>
      )}
    </div>
  );
};

export default StatsDashboard;
